import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import WelcomeModal from '../components/WelcomeModal';
import { phaseApi } from '../services/api';
import { PHASE_GROUPS } from '../components/PhaseNavigation';
import { Check, Lock, ChevronRight, Circle } from 'lucide-react';

// Session storage key to track if welcome has been handled in this browser session
const WELCOME_HANDLED_KEY = 'fsa_welcome_handled';

const StatTile = (props) => (
  <div className="rounded-gc-card border px-4 py-3.5 flex flex-col gap-1" style={{ borderColor: 'var(--gc-border-card)', background: 'var(--gc-card)' }} data-testid={props.testId}>
    <span className="font-plex-mono text-[11px] tracking-[0.08em]" style={{ color: 'var(--gc-text-muted)' }}>{props.label}</span>
    <div className="font-barlow-condensed font-bold text-[26px] leading-none">
      <span style={{ color: 'var(--gc-text-main)' }}>{props.done}</span>
      <span style={{ color: 'var(--gc-text-faint)' }}> / {props.total}</span>
    </div>
  </div>
);

const PhaseRow = (props) => {
  const phase = props.phase;
  const isCurrent = props.isCurrentPhase;
  const isLocked = phase.is_locked;
  const isDone = phase.is_complete && !isLocked;
  const isFree = phase.order_index <= 2 && props.showFreeTag;

  const meta = phase.mode === 'tutorial'
    ? `TUTORIAL · ${phase.item_count || 0} checks`
    : `${phase.ticked_count || 0} / ${phase.item_count || 0} checks`;

  return (
    <button
      onClick={() => !isLocked && props.navigate(`/phases/${phase.slug}`)}
      className="w-full flex items-center gap-3 min-h-[52px] px-3.5 border-b last:border-0 text-left transition-colors"
      style={{ borderColor: 'var(--gc-border-row)', background: isCurrent ? 'var(--gc-current-row)' : 'transparent' }}
      data-testid={`dashboard-phase-row-${phase.slug}`}
    >
      <span
        className="font-plex-mono text-[13px] font-semibold w-6 text-right shrink-0"
        style={{ color: isDone ? 'var(--gc-green)' : isCurrent ? 'var(--gc-cyan)' : 'var(--gc-text-faint)' }}
      >
        {phase.order_index}
      </span>
      <div className="flex-1 min-w-0 flex flex-col">
        <span
          className="font-barlow-condensed font-semibold text-[18px] truncate"
          style={{ color: isLocked ? 'var(--gc-text-muted)' : 'var(--gc-text-main)' }}
        >
          {phase.title}
        </span>
        <span className="font-plex-mono text-[11px]" style={{ color: 'var(--gc-text-muted)' }}>
          {meta}{isFree ? ' · FREE' : ''}
        </span>
      </div>
      {isDone && <Check className="w-4 h-4 shrink-0" style={{ color: 'var(--gc-green)' }} />}
      {!isDone && isCurrent && <Circle className="w-2.5 h-2.5 shrink-0 fill-current" style={{ color: 'var(--gc-cyan)' }} />}
      {isLocked && <Lock className="w-4 h-4 shrink-0" style={{ color: 'var(--gc-text-muted)' }} />}
      {!isDone && !isCurrent && !isLocked && <ChevronRight className="w-4 h-4 shrink-0" style={{ color: 'var(--gc-text-faint)' }} />}
    </button>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(false);
  const [phases, setPhases] = useState([]);

  // Track the initial state of has_seen_welcome when component first mounts
  const initialWelcomeStateRef = useRef(null);
  const welcomeCheckDoneRef = useRef(false);

  const fetchPhases = useCallback(() => {
    phaseApi.getAll().then(res => setPhases(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchPhases();
  }, [fetchPhases]);

  useEffect(() => {
    if (user && !welcomeCheckDoneRef.current) {
      welcomeCheckDoneRef.current = true;

      if (initialWelcomeStateRef.current === null) {
        initialWelcomeStateRef.current = user.has_seen_welcome;
      }

      const welcomeHandledInSession = sessionStorage.getItem(WELCOME_HANDLED_KEY);

      if (!user.has_seen_welcome && user.role !== 'founder' && !welcomeHandledInSession) {
        setShowWelcome(true);
      }
    }
  }, [user]);

  const handleCloseWelcome = async () => {
    setShowWelcome(false);
    sessionStorage.setItem(WELCOME_HANDLED_KEY, 'true');
    try {
      const { authApi } = await import('../services/api');
      await authApi.markWelcomeSeen();
    } catch (err) {
      console.error('Failed to mark welcome as seen:', err);
    }
  };

  const isFounder = user?.role === 'founder';
  const greeting = isFounder || initialWelcomeStateRef.current === true ? 'Welcome back' : 'Welcome';
  // "FREE" tag should only show to users who actually have locked phases (i.e. free users) -
  // never to paid users or the founder. Relies on the backend's own access computation
  // (products + legacy edition entitlement + founder override) rather than re-deriving it.
  const showFreeTag = phases.some(p => p.is_locked);

  const phasesDone = phases.filter(p => p.is_complete && !p.is_locked).length;
  const checksDone = phases.reduce((sum, p) => sum + (p.ticked_count || 0), 0);
  const checksTotal = phases.reduce((sum, p) => sum + (p.item_count || 0), 0);
  const currentPhase = phases.find(p => !p.is_complete && !p.is_locked) || phases[0];

  const groupedPhases = PHASE_GROUPS.map(group => ({
    ...group,
    phases: phases.filter(p => p.order_index >= group.min && p.order_index <= group.max),
  })).filter(group => group.phases.length > 0);

  return (
    <DashboardLayout>
      <WelcomeModal
        isOpen={showWelcome}
        onClose={handleCloseWelcome}
        userName={user?.display_name}
      />

      <div className="max-w-[760px] mx-auto px-5 py-8 md:py-10" data-testid="dashboard-page">
        <span className="font-plex-mono text-[13px] font-semibold tracking-[0.1em]" style={{ color: 'var(--gc-cyan)' }}>
          YOUR FLIGHT
        </span>
        <h1
          className="font-barlow-condensed font-bold text-[34px] md:text-[42px] dt:text-[48px] leading-none mt-2 mb-2"
          style={{ color: 'var(--gc-text-main)' }}
          data-testid="dashboard-heading"
        >
          Cold &amp; dark to shutdown
        </h1>
        <p className="text-base mb-7" style={{ color: 'var(--gc-text-muted)' }} data-testid="dashboard-greeting">
          {greeting}, {user?.display_name || 'Pilot'}.
        </p>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <StatTile label="PHASES" done={phasesDone} total={21} testId="stat-phases" />
          <StatTile label="CHECKS" done={checksDone} total={checksTotal} testId="stat-checks" />
        </div>

        {/* Continue button */}
        {currentPhase && (
          <button
            onClick={() => navigate(`/phases/${currentPhase.slug}`)}
            className="w-full min-h-[60px] rounded-gc-button flex items-center justify-between px-5 mb-4 transition-opacity hover:opacity-90"
            style={{ background: 'var(--gc-cyan)', color: 'var(--gc-cyan-text-on)' }}
            data-testid="continue-phase-btn"
          >
            <div className="flex flex-col items-start">
              <span className="font-plex-mono text-[11px] font-semibold tracking-[0.08em] opacity-80">
                CONTINUE · PHASE {String(currentPhase.order_index).padStart(2, '0')}
              </span>
              <span className="font-barlow-condensed font-bold text-xl">{currentPhase.title}</span>
            </div>
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        {/* Explore the Cockpit card */}
        <button
          onClick={() => navigate('/cockpit')}
          className="w-full flex items-center gap-3 min-h-[64px] px-3.5 rounded-gc-checklist border mb-8 text-left transition-colors hover:opacity-90"
          style={{ borderColor: 'var(--gc-border-card)', background: 'var(--gc-checklist-card)' }}
          data-testid="dashboard-explore-cockpit-card"
        >
          <img
            src="/cockpit/hero.jpg"
            alt="Fenix A320 flight deck"
            className="w-14 h-14 rounded-gc-button object-cover shrink-0"
          />
          <div className="flex-1 min-w-0 flex flex-col">
            <span className="font-barlow-condensed font-semibold text-[18px] truncate" style={{ color: 'var(--gc-text-main)' }}>
              Explore the Cockpit
            </span>
            <span className="font-plex-mono text-[11px]" style={{ color: 'var(--gc-text-muted)' }}>
              The key controls on the Fenix flight deck, marked and explained
            </span>
          </div>
          <ChevronRight className="w-4 h-4 shrink-0" style={{ color: 'var(--gc-text-faint)' }} />
        </button>

        {/* Grouped phase list */}
        <div className="flex flex-col gap-6">
          {groupedPhases.map((group) => (
            <div key={group.name}>
              <div className="flex items-center gap-3 mb-2">
                <span className="font-plex-mono text-[11px] font-semibold tracking-[0.1em]" style={{ color: 'var(--gc-text-muted)' }}>
                  {group.name}
                </span>
                <span className="flex-1 h-px" style={{ background: 'var(--gc-border-nav)' }} />
              </div>
              <div className="rounded-gc-checklist border overflow-hidden" style={{ borderColor: 'var(--gc-border-card)', background: 'var(--gc-checklist-card)' }}>
                {group.phases.map((phase) => (
                  <PhaseRow
                    key={phase.id}
                    phase={phase}
                    isCurrentPhase={currentPhase && phase.id === currentPhase.id}
                    navigate={navigate}
                    showFreeTag={showFreeTag}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
